// The workspace's own spend (#632, D6): what its runs used this period, in
// credits, and — only when it pays for itself — its own line of its own bill.
//
// #469 P3: a customer under an MSP reads its usage in credits and no currency
// amount. The payer is invoiced, one separately totalled line per workspace; this
// screen is the customer's own view, and it never shows another workspace's runs or
// the payer's total. A self-paying workspace reads its own line, priced.

import type { SpendView } from "../api/billing";
import { Chip, Empty, Notice, PageIntro } from "./chrome";

export function SpendPage({ spend, error }: { spend: SpendView | null; error: string | null }) {
  return (
    <>
      <PageIntro
        destination="spend"
        action={spend === null ? null : <Chip>{spend.period}</Chip>}
      />
      {error === null ? null : <Notice tone="bad">{error}</Notice>}
      {spend === null && error === null ? (
        <section className="panel">
          <p className="muted panel__body" role="status">
            Reading this workspace&apos;s spend…
          </p>
        </section>
      ) : null}
      {spend === null ? null : (
        <>
          <section className="panel">
            <div className="panel__head">
              <h2>Credits</h2>
              <Chip>
                {spend.paid_by_own_organisation
                  ? "you pay for this workspace"
                  : "a partner pays for this workspace"}
              </Chip>
            </div>
            <div className="panel__body stack stack--tight">
              <p>
                <b className="tab">{spend.credits_used.toLocaleString()}</b> credits used this
                period
                {spend.cap_credits === null
                  ? " · no cap set by the payer"
                  : ` · of ${spend.cap_credits.toLocaleString()} the payer allows`}
              </p>
              <p className="muted">An investigation is refused when the payer&apos;s pool cannot cover it.</p>
            </div>
          </section>
          {spend.bill === undefined ? null : (
            <section className="panel" data-testid="spend-bill">
              <div className="panel__head">
                <h2>Bill</h2>
                <Chip>{spend.bill.currency}</Chip>
              </div>
              <div className="panel__body stack stack--tight">
                <p>
                  Plan fee {spend.bill.fee} · {spend.bill.included_credits} credits included ·{" "}
                  {spend.bill.overage_credits} over at {spend.bill.overage}
                </p>
                <p>
                  <b className="tab">{spend.bill.total}</b> {spend.bill.currency} for this period
                </p>
                {spend.bill.stand_ins.length === 0 ? null : (
                  <p className="muted">
                    Priced at the platform pair&apos;s rates as a declared stand-in:{" "}
                    {spend.bill.stand_ins.join(", ")}
                  </p>
                )}
              </div>
            </section>
          )}
          <section className="panel">
            <div className="panel__head">
              <h2>Investigations this period</h2>
              <Chip>{spend.runs.length} priced</Chip>
            </div>
            {spend.runs.length === 0 ? (
              <Empty title="No investigation has been priced this period." />
            ) : (
              <div className="table-wrap" tabIndex={0}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Investigation</th>
                      <th className="num">Credits</th>
                      <th>Priced at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spend.runs.map((run) => (
                      <tr key={run.run_id}>
                        <td>
                          <a href={`/runs/${run.run_id}`}>{run.run_id}</a>
                        </td>
                        <td className="num">{run.credits.toLocaleString()}</td>
                        <td>{run.at}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          {spend.chat_turns.length === 0 ? null : (
            <section className="panel">
              <div className="panel__head">
                <h2>Chat turns this period</h2>
                <Chip>{spend.chat_turns.length} priced</Chip>
              </div>
              <div className="table-wrap" tabIndex={0}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Turn</th>
                      <th>On investigation</th>
                      <th className="num">Credits</th>
                      <th>Priced at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spend.chat_turns.map((turn) => (
                      <tr key={turn.turn_id}>
                        <td>{turn.turn_id}</td>
                        <td>
                          <a href={`/runs/${turn.run_id}`}>{turn.run_id}</a>
                        </td>
                        <td className="num">{turn.credits.toLocaleString()}</td>
                        <td>{turn.at}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}
